import React, { useCallback, useMemo, useState } from 'react';
import {
  Button,
  Card,
  CardBody,
  CardTitle,
  DescriptionList,
  DescriptionListDescription,
  DescriptionListGroup,
  DescriptionListTerm,
  Flex,
  FlexItem,
  Label,
  Switch,
  ToolbarItem,
  Tooltip,
} from '@patternfly/react-core';
import { Table, Tbody, Td, Th, Thead, Tr } from '@patternfly/react-table';
import { CogIcon, DownloadIcon, UsersIcon } from '@patternfly/react-icons';
import { useNotifications } from '../../../contexts/NotificationContext';
import { useAccountingEntries, useWinAuditorOdExport } from '../../../hooks/useAdminReconciliation';
import type { AccountingEntriesShow, AccountingEntry, AccountingLine } from '../../../types/models/Reconciliation';
import { formatBareDateNumeric, formatInstantBrussels } from '../../../utils/dateFormat';
import { StatusMultiSelect } from '../Verda/VerdaGpuParts';
import {
  AMOUNT_CELL,
  CheckLabel,
  CsvButton,
  EmptyListState,
  FetchedAt,
  FlagLabels,
  ListPagination,
  ListToolbar,
  LiveReadSpinner,
  Money,
  MonthPicker,
  NoMatchState,
  ReadErrorState,
  ReconciliationBanners,
  RefreshButton,
  StaleDataAlert,
  distinctOptions,
  matchesQuery,
  useClientList,
} from './ReconciliationParts';
import {
  ENTRY_ACTION_FLAGS,
  ENTRY_FLAG_INFO,
  ENTRY_KIND_LABELS,
  ENTRY_STATUS_LABELS,
  ENTRY_STATUS_MEANINGS,
  JOURNAL_LABELS,
  WINAUDITOR_STATUS_LABELS,
  labelOf,
} from './reconciliationLabels';
import { buildAccountingEntriesCsv, reconciliationCsvFilename } from './reconciliationCsv';
import AccountingAccountsModal from './AccountingAccountsModal';
import WinAuditorCustomerCodesModal from './WinAuditorCustomerCodesModal';
import { missingCodeCount } from './customerCodesForm';
import { isExportableJournal, odExportBlocker } from './winauditorOd';

/** Journals in the backend's sort order (the filter lists them this way, not alphabetically). */
const JOURNAL_ORDER = ['bank', 'purchases', 'sales', 'misc'];

/**
 * The broad search: the entry's reference and description, and its lines' accounts (and WinAuditor codes),
 * labels, comments, customers and settles text.
 */
export const matchEntry = (e: AccountingEntry, q: string) =>
  matchesQuery(q, [
    e.reference,
    e.description,
    e.lines.flatMap((l) => [
      l.account,
      l.account_label ?? '',
      l.winauditor_account?.code ?? '',
      l.label,
      l.comment ?? '',
      l.settles.text,
      l.client?.name ?? '',
      l.client?.vat_number ?? '',
    ]),
  ]);

const COLUMN_COUNT = 6;

/** The entry's status: « À encoder » (orange, an action) or « À vérifier » (blue), with its meaning. */
const StatusLabel: React.FunctionComponent<{ status: string }> = ({ status }) => {
  const meaning = ENTRY_STATUS_MEANINGS[status];
  const label = (
    <Label color={status === 'to_encode' ? 'orange' : 'blue'} isCompact>
      {labelOf(ENTRY_STATUS_LABELS, status)}
    </Label>
  );
  return meaning ? <Tooltip content={meaning}>{label}</Tooltip> : label;
};

/** Facturier's latest WinAuditor export of a sales document. */
function winauditorText(w: AccountingEntry['winauditor']): string | null {
  if (w === null) {
    return null;
  }
  if (w.status === null) {
    return 'Jamais exportée vers WinAuditor';
  }
  const label = labelOf(WINAUDITOR_STATUS_LABELS, w.status);
  return w.at === null ? label : `${label} le ${formatInstantBrussels(w.at)}`;
}

/**
 * How WinAuditor encodes the line, when it is not simply the general account itself: a customer's own code
 * (`C 0000004`, or « code manquant »), Mollie's supplier code (`F 125`).
 */
const WinAuditorAccountText: React.FunctionComponent<{ line: AccountingLine }> = ({ line }) => {
  const wa = line.winauditor_account;
  if (!wa || (wa.type === 'G' && wa.code === line.account)) {
    return null;
  }
  if (wa.code === null) {
    return (
      <div>
        <Label status="danger" isCompact>
          {wa.type} — code WinAuditor manquant
        </Label>
      </div>
    );
  }
  return (
    <div className="pf-v6-u-font-size-sm">
      WinAuditor : {wa.type} {wa.code}
    </div>
  );
};

/** « Exporter pour WinAuditor » on an OD — aria-disabled with its reason while a customer code is missing. */
const OdExportButton: React.FunctionComponent<{
  entry: AccountingEntry;
  isExporting: boolean;
  isBusy: boolean;
  onExport: (entry: AccountingEntry) => void;
}> = ({ entry, isExporting, isBusy, onExport }) => {
  const blocker = odExportBlocker(entry);
  const button = (
    <Button
      variant="secondary"
      size="sm"
      icon={<DownloadIcon />}
      isLoading={isExporting}
      isAriaDisabled={blocker !== null || isBusy}
      onClick={() => onExport(entry)}
      aria-label={`Exporter pour WinAuditor : ${entry.description}`}
    >
      Exporter pour WinAuditor
    </Button>
  );
  return blocker === null ? button : <Tooltip content={blocker}>{button}</Tooltip>;
};

/** One entry: its header row (date · journal · reference · description · status · flags), then its lines. */
const EntryRows: React.FunctionComponent<{
  entry: AccountingEntry;
  /** The WinAuditor OD export (journal `misc` only); absent → no export button. */
  odExport?: { exportingId: string | null; onExport: (entry: AccountingEntry) => void };
}> = ({ entry, odExport }) => {
  const winauditor = winauditorText(entry.winauditor);
  return (
    <>
      <Tr className="ancalagon-accounting-entry-header">
        <Td colSpan={COLUMN_COUNT}>
          <Flex
            alignItems={{ default: 'alignItemsCenter' }}
            spaceItems={{ default: 'spaceItemsSm' }}
            flexWrap={{ default: 'wrap' }}
          >
            <FlexItem>
              <strong>{formatBareDateNumeric(entry.date)}</strong>
            </FlexItem>
            <FlexItem>{labelOf(JOURNAL_LABELS, entry.journal)}</FlexItem>
            {entry.reference && (
              <FlexItem>
                <strong>{entry.reference}</strong>
              </FlexItem>
            )}
            <FlexItem>{entry.description}</FlexItem>
            <FlexItem>
              <StatusLabel status={entry.status} />
            </FlexItem>
            {!entry.balanced && (
              <FlexItem>
                <Label status="danger" isCompact>
                  Déséquilibrée
                </Label>
              </FlexItem>
            )}
            {entry.flags.length > 0 && (
              <FlexItem>
                <FlagLabels flags={entry.flags} catalog={ENTRY_FLAG_INFO} severe={ENTRY_ACTION_FLAGS} />
              </FlexItem>
            )}
            {winauditor && <FlexItem className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">{winauditor}</FlexItem>}
            {odExport && isExportableJournal(entry) && (
              <FlexItem align={{ default: 'alignRight' }}>
                <OdExportButton
                  entry={entry}
                  isExporting={odExport.exportingId === entry.id}
                  isBusy={odExport.exportingId !== null}
                  onExport={odExport.onExport}
                />
              </FlexItem>
            )}
          </Flex>
        </Td>
      </Tr>
      {entry.lines.map((line, i) => (
        <Tr key={i}>
          <Td dataLabel="Compte" modifier="nowrap">
            <strong>{line.account}</strong>
            {line.account_label && (
              <div className="pf-v6-u-font-size-sm pf-v6-u-text-color-subtle">{line.account_label}</div>
            )}
            <WinAuditorAccountText line={line} />
          </Td>
          <Td dataLabel="Libellé" modifier="breakWord">
            {line.label}
          </Td>
          <Td dataLabel="Débit" className={AMOUNT_CELL}>
            {line.debit === null ? '' : <Money value={line.debit} />}
          </Td>
          <Td dataLabel="Crédit" className={AMOUNT_CELL}>
            {line.credit === null ? '' : <Money value={line.credit} />}
          </Td>
          <Td dataLabel="Commentaire" modifier="nowrap">
            {line.comment}
          </Td>
          <Td dataLabel="Répartition" modifier="breakWord">
            {line.settles.text}
          </Td>
        </Tr>
      ))}
    </>
  );
};

/** « Totaux par compte » — a plain table (Bruno: about ten accounts a month, no search or pagination). */
const ByAccountCard: React.FunctionComponent<{ data: AccountingEntriesShow }> = ({ data }) => (
  <Card className="pf-v6-u-mt-lg">
    <CardTitle>Totaux par compte</CardTitle>
    <CardBody>
      {data.by_account.length === 0 ? (
        <EmptyListState title="Aucun compte mouvementé ce mois-ci" />
      ) : (
        <Table aria-label="Totaux par compte du mois" variant="compact">
          <Thead>
            <Tr>
              <Th>Compte</Th>
              <Th>Intitulé</Th>
              <Th className={AMOUNT_CELL}>Débit</Th>
              <Th className={AMOUNT_CELL}>Crédit</Th>
            </Tr>
          </Thead>
          <Tbody>
            {data.by_account.map((a) => (
              <Tr key={a.account}>
                <Td dataLabel="Compte">
                  <strong>{a.account}</strong>
                </Td>
                <Td dataLabel="Intitulé">{a.label ?? '—'}</Td>
                <Td dataLabel="Débit" className={AMOUNT_CELL}>
                  <Money value={a.debit} />
                </Td>
                <Td dataLabel="Crédit" className={AMOUNT_CELL}>
                  <Money value={a.credit} />
                </Td>
              </Tr>
            ))}
            <Tr>
              <Td dataLabel="Compte">
                <strong>Total</strong>
              </Td>
              <Td dataLabel="Intitulé" />
              <Td dataLabel="Débit" className={AMOUNT_CELL}>
                <strong>
                  <Money value={data.totals.debit} />
                </strong>
              </Td>
              <Td dataLabel="Crédit" className={AMOUNT_CELL}>
                <strong>
                  <Money value={data.totals.credit} />
                </strong>
              </Td>
            </Tr>
          </Tbody>
        </Table>
      )}
    </CardBody>
  </Card>
);

interface AccountingEntriesTabProps {
  month: string;
  onMonthChange: (month: string) => void;
  isActive: boolean;
}

/**
 * Comptabilité — `GET /admin/reconciliation/accounting-entries`: the month's entries Sophie must encode or
 * check in WinAuditor, one block per entry (header, then its lines on PCMN accounts with their WinAuditor
 * encoding, debit / credit, WinAuditor comment and « la répartition »), the totals per account, and two
 * settings modals: the chart of accounts (« Plan comptable ») and the month's customer codes (« Codes clients
 * WinAuditor »). Each OD (journal `misc`) downloads as its WinAuditor import file. Paginated by ENTRY so an
 * entry's lines never split over two pages.
 */
const AccountingEntriesTab: React.FunctionComponent<AccountingEntriesTabProps> = ({
  month,
  onMonthChange,
  isActive,
}) => {
  const { data, loading, error, fetchedAt, refetch } = useAccountingEntries(month, isActive);
  const rows = useMemo(() => data?.entries ?? [], [data]);
  const [statuses, setStatuses] = useState<string[]>([]);
  const [journals, setJournals] = useState<string[]>([]);
  const [kinds, setKinds] = useState<string[]>([]);
  const [flaggedOnly, setFlaggedOnly] = useState(false);
  const [isAccountsOpen, setIsAccountsOpen] = useState(false);
  const [isCustomersOpen, setIsCustomersOpen] = useState(false);
  const customers = useMemo(() => data?.customers ?? [], [data]);
  const missingCodes = missingCodeCount(customers);
  const { exportOd, exportingId } = useWinAuditorOdExport();
  const { addNotification, setDrawerOpen } = useNotifications();

  const handleExport = useCallback(
    async (entry: AccountingEntry) => {
      const result = await exportOd(month, entry);
      if (!result.ok) {
        addNotification({
          title: "Impossible d'exporter l'OD pour WinAuditor",
          srTitle: 'Error alert',
          description: result.message,
          variant: 'danger',
        });
        setDrawerOpen(true);
      }
    },
    [exportOd, month, addNotification, setDrawerOpen],
  );
  const odExport = useMemo(
    () => ({ exportingId, onExport: (entry: AccountingEntry) => void handleExport(entry) }),
    [exportingId, handleExport],
  );

  const statusOptions = useMemo(
    () =>
      ['to_encode', 'to_check']
        .filter((s) => rows.some((r) => r.status === s) || statuses.includes(s))
        .map((s) => ({ value: s, label: labelOf(ENTRY_STATUS_LABELS, s) })),
    [rows, statuses],
  );
  const journalOptions = useMemo(() => {
    const present = new Set<string>([...rows.map((r) => r.journal), ...journals]);
    return [
      ...JOURNAL_ORDER.filter((j) => present.has(j)),
      ...Array.from(present).filter((j) => !JOURNAL_ORDER.includes(j)),
    ].map((j) => ({ value: j, label: labelOf(JOURNAL_LABELS, j) }));
  }, [rows, journals]);
  const kindOptions = useMemo(
    () => distinctOptions([...rows.map((r) => r.kind), ...kinds], (v) => labelOf(ENTRY_KIND_LABELS, v)),
    [rows, kinds],
  );

  const predicate = useCallback(
    (e: AccountingEntry) =>
      (statuses.length === 0 || statuses.includes(e.status)) &&
      (journals.length === 0 || journals.includes(e.journal)) &&
      (kinds.length === 0 || kinds.includes(e.kind)) &&
      (!flaggedOnly || e.flags.length > 0),
    [statuses, journals, kinds, flaggedOnly],
  );
  const filterKey =
    statuses.length + journals.length + kinds.length > 0 || flaggedOnly
      ? JSON.stringify([statuses, journals, kinds, flaggedOnly])
      : '';
  const list = useClientList(rows, matchEntry, predicate, filterKey);

  const clearFilters = () => {
    list.setSearchValue('');
    setStatuses([]);
    setJournals([]);
    setKinds([]);
    setFlaggedOnly(false);
  };

  const header = (
    <Flex
      alignItems={{ default: 'alignItemsCenter' }}
      spaceItems={{ default: 'spaceItemsMd' }}
      className="pf-v6-u-mb-md"
    >
      <FlexItem>
        <MonthPicker month={month} onChange={onMonthChange} />
      </FlexItem>
      <FlexItem>
        <RefreshButton loading={loading} onRefresh={() => void refetch()} />
      </FlexItem>
      <FlexItem>
        <FetchedAt fetchedAt={fetchedAt} />
      </FlexItem>
      <FlexItem align={{ default: 'alignRight' }}>
        <Flex spaceItems={{ default: 'spaceItemsSm' }}>
          {customers.length > 0 && (
            <FlexItem>
              <Button
                variant="secondary"
                icon={<UsersIcon />}
                onClick={() => setIsCustomersOpen(true)}
                countOptions={missingCodes > 0 ? { count: missingCodes, isRead: false } : undefined}
              >
                Codes clients WinAuditor
              </Button>
            </FlexItem>
          )}
          <FlexItem>
            <Button variant="secondary" icon={<CogIcon />} onClick={() => setIsAccountsOpen(true)}>
              Plan comptable
            </Button>
          </FlexItem>
        </Flex>
      </FlexItem>
    </Flex>
  );

  const modal = (
    <>
      <AccountingAccountsModal
        isOpen={isAccountsOpen}
        onClose={() => setIsAccountsOpen(false)}
        onSaved={() => void refetch()}
      />
      <WinAuditorCustomerCodesModal
        isOpen={isCustomersOpen}
        onClose={() => setIsCustomersOpen(false)}
        customers={customers}
        onSaved={() => void refetch()}
      />
    </>
  );

  if (data === null) {
    return (
      <>
        {header}
        {error !== null && !loading ? (
          <ReadErrorState
            title="Impossible de charger les écritures comptables"
            message={error}
            onRetry={() => void refetch()}
          />
        ) : (
          <LiveReadSpinner what="écritures comptables" />
        )}
        {modal}
      </>
    );
  }

  const { totals, check } = data;
  const filters = (
    <>
      <ToolbarItem>
        <StatusMultiSelect
          options={statusOptions}
          selected={statuses}
          onChange={setStatuses}
          ariaLabel="Filtrer par statut"
          label="Statut"
        />
      </ToolbarItem>
      <ToolbarItem>
        <StatusMultiSelect
          options={journalOptions}
          selected={journals}
          onChange={setJournals}
          ariaLabel="Filtrer par journal"
          label="Journal"
        />
      </ToolbarItem>
      <ToolbarItem>
        <StatusMultiSelect
          options={kindOptions}
          selected={kinds}
          onChange={setKinds}
          ariaLabel="Filtrer par type d'écriture"
          label="Type d'écriture"
        />
      </ToolbarItem>
      <ToolbarItem alignSelf="center">
        <Switch
          id="entries-flagged-only"
          label="Signalées seulement"
          isChecked={flaggedOnly}
          onChange={(_e, checked) => setFlaggedOnly(checked)}
        />
      </ToolbarItem>
    </>
  );

  return (
    <>
      {header}
      {modal}
      {error !== null && <StaleDataAlert message={error} />}
      <ReconciliationBanners
        warnings={data.warnings}
        context={{ view: 'entries', period: data.period, complete: data.complete, entriesCheck: check }}
        onRetry={() => void refetch()}
      />
      <DescriptionList
        isCompact
        isHorizontal
        columnModifier={{ default: '1Col', md: '2Col', xl: '3Col' }}
        className="pf-v6-u-mb-md"
      >
        <DescriptionListGroup>
          <DescriptionListTerm>Écritures du mois</DescriptionListTerm>
          <DescriptionListDescription>
            {totals.entries} ({totals.lines} lignes)
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>À encoder · À vérifier</DescriptionListTerm>
          <DescriptionListDescription>
            <strong>{totals.to_encode}</strong> · {totals.to_check}
          </DescriptionListDescription>
        </DescriptionListGroup>
        <DescriptionListGroup>
          <DescriptionListTerm>Débit · Crédit</DescriptionListTerm>
          <DescriptionListDescription>
            <Money value={totals.debit} /> · <Money value={totals.credit} />{' '}
            <CheckLabel ok={check.balanced && check.unbalanced_entries === 0} />
          </DescriptionListDescription>
        </DescriptionListGroup>
      </DescriptionList>
      {rows.length === 0 ? (
        <EmptyListState title="Aucune écriture ce mois-ci" />
      ) : (
        <>
          <ListToolbar
            list={list}
            placeholder="Rechercher par pièce, description, client, n° de TVA, compte ou répartition..."
            filters={filters}
            itemsLabel="écritures"
            actions={
              <CsvButton
                label="Exporter le mois (CSV)"
                filename={reconciliationCsvFilename.accountingEntries(month)}
                build={() => buildAccountingEntriesCsv(rows)}
              />
            }
          />
          {list.filtered.length === 0 ? (
            <NoMatchState onClear={clearFilters} />
          ) : (
            <>
              <Table aria-label="Écritures comptables du mois" variant="compact">
                <Thead>
                  <Tr>
                    <Th width={15}>Compte</Th>
                    <Th>Libellé</Th>
                    <Th className={AMOUNT_CELL} width={10}>
                      Débit
                    </Th>
                    <Th className={AMOUNT_CELL} width={10}>
                      Crédit
                    </Th>
                    <Th>Commentaire</Th>
                    <Th>Répartition</Th>
                  </Tr>
                </Thead>
                {list.paginated.map((entry) => (
                  // A GPU entry id can come back in another month's view: key on the month too.
                  <Tbody key={`${month}-${entry.id}`}>
                    <EntryRows entry={entry} odExport={odExport} />
                  </Tbody>
                ))}
              </Table>
              <ListPagination list={list} variant="bottom" itemsLabel="écritures" />
            </>
          )}
        </>
      )}
      <ByAccountCard data={data} />
    </>
  );
};

export default AccountingEntriesTab;
